export type ConnectAiGuideGroup = 'chat' | 'tools';

export type ConnectAiGuideId =
  | 'chatgpt'
  | 'claude'
  | 'grok'
  | 'gemini'
  | 'claude-code'
  | 'codex'
  | 'cursor'
  | 'antigravity'
  | 'other';

export type ConnectAiGuide = {
  id: ConnectAiGuideId;
  group: ConnectAiGuideGroup;
  title: string;
  need: string;
  docsHref?: string;
  docsLabel?: string;
  warning?: string;
  steps: string[];
  after: string[];
  command?: string;
  copyLabel?: string;
};

export const CONNECT_AI_GROUPS: { id: ConnectAiGuideGroup; label: string }[] = [
  { id: 'chat', label: 'Chat apps' },
  { id: 'tools', label: 'Coding tools' },
];

export const geminiCliCommand = (mcpUrl: string) => (
  `gemini mcp add -s user -t http autoclipper ${mcpUrl}`
);

export const claudeCodeCommand = (mcpUrl: string) => (
  `claude mcp add --scope user --transport http autoclipper ${mcpUrl}`
);

export const codexCommand = (mcpUrl: string) => (
  `codex mcp add autoclipper --url ${mcpUrl}`
);

export const genericMcpJson = (mcpUrl: string) => JSON.stringify({
  mcpServers: {
    autoclipper: { url: mcpUrl },
  },
}, null, 2);

export const cursorMcpJson = genericMcpJson;

export const antigravityMcpJson = (mcpUrl: string) => JSON.stringify({
  mcpServers: {
    autoclipper: { serverUrl: mcpUrl },
  },
}, null, 2);

export const codingToolsSetupPrompt = (mcpUrl: string): string => {
  const tools = connectAiGuides(mcpUrl).filter((guide) => guide.group === 'tools');
  const sections = tools.map((guide) => {
    const lines = [
      `### ${guide.title}`,
      `If you are ${guide.title}, follow only this section.`,
      guide.warning ? `Warning: ${guide.warning}` : null,
      guide.command
        ? `Use this config or command if Autoclipper is missing:\n${guide.command}`
        : null,
    ].filter((line): line is string => Boolean(line));
    return lines.join('\n');
  });

  return [
    'Configure Autoclipper as a remote Streamable HTTP MCP server in THIS tool only.',
    'This task is idempotent.',
    `MCP URL: ${mcpUrl}`,
    'Preferred server name: autoclipper (display name Autoclipper is fine).',
    '',
    'Inspect this tool\'s existing MCP or connector config first.',
    'If Autoclipper / autoclipper is already present with this exact MCP URL, do not duplicate, rewrite, or re-register it. Confirm it is enabled and stop.',
    'If it is present with a different URL, update only the URL to this one.',
    'If it is missing, add it using the matching section below, then authenticate only if this tool asks.',
    'Do not add an OAuth Client ID or Client Secret. Autoclipper registers itself.',
    'Do not configure other coding tools on this machine.',
    '',
    ...sections,
    '',
    'Reply with one sentence: already connected, or added (complete Autoclipper login if a browser opened).',
  ].join('\n');
};

export const connectAiGuides = (mcpUrl: string): ConnectAiGuide[] => [
  {
    id: 'chatgpt',
    group: 'chat',
    title: 'ChatGPT',
    need: 'A paid ChatGPT plan (Plus, Pro, Business, Enterprise, or Edu). Custom connectors are not available on the free ChatGPT plan.',
    docsHref: 'https://chatgpt.com/',
    docsLabel: 'Open ChatGPT',
    steps: [
      'Open chatgpt.com and sign in.',
      'Click your profile, then Settings → Apps & Connectors (sometimes labeled Connectors).',
      'Open Advanced settings and turn Developer Mode on. Accept the warning about third-party connectors.',
      'Go back to Apps & Connectors and click Create or Add custom connector.',
      'Name: Autoclipper.',
      `MCP / connector URL: ${mcpUrl}`,
      'Authentication: OAuth. Do not choose Token or None.',
      'Leave OAuth Client ID and Client Secret blank. Autoclipper registers itself when ChatGPT connects.',
      'Save, then complete Autoclipper login in the browser window and click Allow.',
    ],
    after: [
      'Start a new chat.',
      'Open the tools / apps menu and enable Autoclipper for that conversation.',
      'Ask ChatGPT to create clips from a YouTube URL.',
    ],
  },
  {
    id: 'claude',
    group: 'chat',
    title: 'Claude',
    need: 'claude.ai on Free, Pro, or Max. Free plans can add one custom connector. Team and Enterprise need an Owner to add the connector first.',
    docsHref: 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
    docsLabel: 'Claude custom connector help',
    warning: 'Leave Advanced settings empty. Do not paste an OAuth Client ID or secret — Claude registers with Autoclipper automatically.',
    steps: [
      'Open claude.ai and sign in.',
      'Go to Customize → Connectors (or Settings → Connectors).',
      'Click + and choose Add custom connector.',
      'Name: Autoclipper.',
      `URL: ${mcpUrl}`,
      'Leave Advanced settings / OAuth Client ID and Client Secret blank.',
      'Click Add, then Connect.',
      'Sign in to Autoclipper in the browser window and click Allow.',
    ],
    after: [
      'Open a new Claude chat.',
      'Click + → Connectors and turn Autoclipper on for that conversation.',
      'Ask Claude to create clips from a YouTube URL.',
    ],
  },
  {
    id: 'grok',
    group: 'chat',
    title: 'Grok',
    need: 'A grok.com account. On Grok Business / Enterprise, a team admin may need to allow custom connectors first.',
    docsHref: 'https://grok.com/connectors',
    docsLabel: 'Open Grok connectors',
    steps: [
      'Open grok.com/connectors.',
      'Click New Connector, then Custom.',
      'Name: Autoclipper.',
      `MCP server URL: ${mcpUrl}`,
      'Save the connector. If Grok asks for a client secret, leave it blank.',
      'Complete Autoclipper login in the browser window and click Allow.',
    ],
    after: [
      'Start a new Grok conversation.',
      'Enable the Autoclipper connector if Grok asks which tools to use.',
      'Ask Grok to create clips from a YouTube URL.',
    ],
  },
  {
    id: 'gemini',
    group: 'chat',
    title: 'Gemini',
    need: 'Gemini CLI on your computer. The consumer Gemini website does not add arbitrary custom MCP servers the same way ChatGPT and Claude do.',
    docsHref: 'https://google-gemini.github.io/gemini-cli/docs/tools/mcp-server.html',
    docsLabel: 'Gemini CLI MCP docs',
    steps: [
      'Install Gemini CLI if you do not already have it.',
      'Copy the command below and run it in a terminal. It writes Autoclipper into your user Gemini config.',
      'Run gemini. The CLI will open a browser the first time it needs Autoclipper.',
      'Sign in to Autoclipper and click Allow.',
    ],
    after: [
      'In Gemini CLI, run /mcp and confirm autoclipper is listed.',
      'Ask Gemini to create clips from a YouTube URL.',
    ],
    command: geminiCliCommand(mcpUrl),
    copyLabel: 'Copy command',
  },
  {
    id: 'claude-code',
    group: 'tools',
    title: 'Claude Code',
    need: 'The Claude Code CLI (`claude`) on your computer. This is separate from claude.ai in the Chat apps tab.',
    docsHref: 'https://code.claude.com/docs/en/mcp',
    docsLabel: 'Claude Code MCP docs',
    steps: [
      'Install Claude Code if you do not already have it.',
      'Copy the command below and run it in a terminal, not inside a Claude Code chat. --scope user makes Autoclipper available in every project.',
      'Start Claude Code, then run /mcp.',
      'Select autoclipper and authenticate if Claude Code asks. Sign in to Autoclipper and click Allow.',
    ],
    after: [
      'Run /mcp and confirm autoclipper is connected.',
      'Ask Claude Code to create clips from a YouTube URL.',
    ],
    command: claudeCodeCommand(mcpUrl),
    copyLabel: 'Copy command',
  },
  {
    id: 'codex',
    group: 'tools',
    title: 'Codex',
    need: 'Codex CLI, the ChatGPT desktop app, or the Codex IDE extension. They share ~/.codex/config.toml.',
    docsHref: 'https://learn.chatgpt.com/docs/extend/mcp',
    docsLabel: 'Codex MCP docs',
    steps: [
      'Copy the command below and run it in a terminal. Codex adds a Streamable HTTP server named autoclipper.',
      'Do not pass --oauth-client-id. Autoclipper registers itself (DCR).',
      'If a browser window did not open, run: codex mcp login autoclipper',
      'Sign in to Autoclipper and click Allow.',
      'You can also add the same URL in ChatGPT desktop or the Codex IDE extension: Settings → MCP servers → Add server → Streamable HTTP.',
    ],
    after: [
      'Run codex mcp list, or /mcp inside Codex, and confirm autoclipper is enabled.',
      'Ask Codex to create clips from a YouTube URL.',
    ],
    command: codexCommand(mcpUrl),
    copyLabel: 'Copy command',
  },
  {
    id: 'cursor',
    group: 'tools',
    title: 'Cursor',
    need: 'Cursor desktop (or Cursor CLI). Custom MCP servers live in ~/.cursor/mcp.json or .cursor/mcp.json in a project.',
    docsHref: 'https://cursor.com/docs/mcp',
    docsLabel: 'Cursor MCP docs',
    warning: 'Do not add an auth CLIENT_ID or CLIENT_SECRET. Cursor registers with Autoclipper automatically.',
    steps: [
      'Open Cursor Settings, then MCP (also listed under Customize → MCP).',
      'Click Add new global MCP server, or open ~/.cursor/mcp.json.',
      'Paste the JSON below. If you use the form instead: Name Autoclipper, URL is the MCP URL on this page, transport Streamable HTTP / remote.',
      'Save. When Cursor asks you to authenticate, sign in to Autoclipper and click Allow.',
    ],
    after: [
      'In Customize → MCP, confirm Autoclipper is enabled.',
      'Ask Agent to create clips from a YouTube URL.',
    ],
    command: cursorMcpJson(mcpUrl),
    copyLabel: 'Copy JSON',
  },
  {
    id: 'antigravity',
    group: 'tools',
    title: 'Antigravity',
    need: 'Google Antigravity (IDE or the agy CLI). Config is shared at ~/.gemini/config/mcp_config.json.',
    docsHref: 'https://antigravity.google/docs/cli/mcp/',
    docsLabel: 'Antigravity MCP docs',
    warning: 'Antigravity requires serverUrl. Do not use url or httpUrl — those keys are ignored.',
    steps: [
      'In Antigravity IDE: open … on the agent panel → MCP Servers → Manage MCP Servers → View raw config. In Antigravity 2.0 you can also use Settings → Customizations → Installed MCP Servers.',
      'Or edit ~/.gemini/config/mcp_config.json (all projects) or .agents/mcp_config.json (this workspace).',
      'Paste the JSON below. Merge into an existing mcpServers object if that file already has other servers.',
      'Authenticate: Agent Settings → Authenticate next to Autoclipper. In the agy CLI, run /mcp and choose Authenticate.',
      'Sign in to Autoclipper and click Allow. If the CLI asks for a code, paste it back into the prompt.',
    ],
    after: [
      'In agy, run /mcp and confirm autoclipper is connected.',
      'Ask Antigravity to create clips from a YouTube URL.',
    ],
    command: antigravityMcpJson(mcpUrl),
    copyLabel: 'Copy JSON',
  },
  {
    id: 'other',
    group: 'tools',
    title: 'Other tools',
    need: 'Any MCP client that can add a remote Streamable HTTP server: VS Code / GitHub Copilot, Windsurf, Cline, Continue, Zed, Copilot CLI, and similar.',
    docsHref: 'https://modelcontextprotocol.io/clients',
    docsLabel: 'MCP client list',
    warning: 'Choose OAuth (not a static token). Leave Client ID and secret blank unless the tool cannot do Dynamic Client Registration.',
    steps: [
      'Open that tool’s MCP / connectors / tools settings.',
      'Add a custom remote server. Transport: Streamable HTTP (sometimes labeled HTTP or SSE).',
      'Name: Autoclipper.',
      `URL: ${mcpUrl}`,
      'Authentication: OAuth. Leave Client ID and Client Secret empty.',
      'Save, then complete Autoclipper login in the browser and click Allow.',
      'VS Code / Copilot often uses .vscode/mcp.json with type "http" and the same URL. Windsurf, Cline, Continue, and Zed usually take the JSON below in their MCP config file.',
    ],
    after: [
      'Confirm Autoclipper is enabled for the current chat or agent session.',
      'Ask the assistant to create clips from a YouTube URL.',
    ],
    command: genericMcpJson(mcpUrl),
    copyLabel: 'Copy JSON',
  },
];
